import type {DataProviderState} from './data-provider-state.interface.js';
import type {CrosspointDataProvider} from '@o2d/datasource';
import type {DateTime} from 'luxon';
import type {DataProviderStateManagerInterface} from './data-provider-state-manager.interface.js';
import {MatrixRanges} from "@o2d/grid-layout";

/**
 * Manages state for multiple DataProviders in a timeline.
 *
 * Responsibilities:
 * - Store cursor + span per provider
 * - Clamp cursor on scroll (prevent viewport outside TYAxisValue-axis absoluteBoundaries)
 * - Re-clamp on span change (resize)
 * - Compute viewport range from cursor + span
 * - Auto-adjust cursor to show all elements when they fit in viewport
 *
 * Key Concepts:
 * - cursorIndex = viewport start position (relative to Index 0)
 * - span = viewport size (constant until resize)
 * - Viewport range = [cursor, cursor+span-1]
 * - TYAxisValue-axis is clamped (finite), TXAxisValue-axis is unclamped (infinite timeline)
 */
export class DataProviderStateManager implements DataProviderStateManagerInterface<DateTime, unknown, unknown> {

    private _version = 0;

    private states = new Map<string, DataProviderState>();

    private providers = new Map<string, CrosspointDataProvider<DateTime, unknown, unknown>>();

    private providerByAxesIdsMap = new Map<string, Map<string, string>>();

    /**
     * Version counter that increments on each state change.
     * Used by Lit components to trigger reactive updates.
     */
    get version(): number {
        return this._version;
    }

    /**
     * Calculates the optimal cursor position for an axis.
     * If all elements fit within the viewport (totalElements <= span),
     * the cursor is set to the minimum boundary to show all elements.
     * Otherwise, the cursor defaults to 0 (origin).
     *
     * @param min - Minimum boundary of the axis
     * @param max - Maximum boundary of the axis
     * @param span - Available viewport size (number of cells)
     * @returns Optimal cursor index
     */
    private calculateOptimalCursor(min: number, max: number, span: number): number {
        // Only adjust if boundaries are finite (not infinite axis)
        if (!Number.isFinite(min) || !Number.isFinite(max)) {
            return 0;
        }

        const totalElements = max - min + 1;

        // If all elements fit in viewport, start at minimum to show all
        if (totalElements <= span) {
            return min;
        }

        // Otherwise, start at origin
        return 0;
    }

    /**
     * Registers a data provider with specified dimensions and associates it with a unique key.
     *
     * @param {string} key - The unique identifier for the data provider being registered.
     * @param {CrosspointDataProvider<DateTime, unknown, unknown>} provider - The data provider instance to be registered.
     * @param {number} spanX - The horizontal span (number of logical units) covered by the data provider.
     * @param {number} spanY - The vertical span (number of logical units) covered by the data provider.
     * @return {void} This method does not return a value.
     * @throws {Error} Throws an error if a provider with the specified key is already registered.
     */
    registerProvider(
        key: string,
        provider: CrosspointDataProvider<DateTime, unknown, unknown>,
        spanX: number,
        spanY: number
    ): void {
        if (this.providers.has(key)) {
            throw new Error(`Provider "${key}" already registered`);
        }

        this.providers.set(key, provider);

        // Calculate optimal cursor positions based on axis boundaries and viewport size.
        // If all elements fit in the viewport, start at the minimum boundary.
        // Otherwise, start at origin (Index 0).
        const [minX, maxX] = provider.axisX.getBoundaries();
        const [minY, maxY] = provider.axisY.getBoundaries();

        const cursorIndexX = this.calculateOptimalCursor(minX, maxX, spanX);
        const cursorIndexY = this.calculateOptimalCursor(minY, maxY, spanY);

        this.states.set(key, {
            cursorIndexX,
            cursorIndexY,
            spanX,
            spanY
        });

        const axisIdX = provider.axisX.axisId;
        const axisIdY = provider.axisY.axisId;

        if (!this.providerByAxesIdsMap.has(axisIdX)) {
            this.providerByAxesIdsMap.set(axisIdX, new Map());
        }
        this.providerByAxesIdsMap.get(axisIdX)!.set(axisIdY, key);
    }

    /**
     * Checks if a provider is registered.
     *
     * @param key - Provider identifier
     * @returns True if provider exists
     */
    hasProvider(key: string): boolean {
        return this.providers.has(key);
    }

    /**
     * Checks if any provider is registered.
     *
     * @returns True if at least one provider exists
     */
    hasAnyProvider(): boolean {
        return this.providers.size > 0;
    }

    /**
     * Updates the cursor position on a specific axis for a given data provider.
     *
     * @param {string} dataProviderId - The identifier of the data provider to update the cursor for.
     * @param {'x' | 'y'} axis - The axis to update the cursor on ('x' or 'y').
     * @param {number} delta - The amount to adjust the cursor position by.
     * @return {void} - This method does not return a value.
     * @throws {Error} - Throws an error if the specified data provider is not registered.
     */
    updateCursorByAxisId(dataProviderId: string, axis: 'x' | 'y', delta: number): void {
        const state = this.states.get(dataProviderId);
        if (!state) {
            throw new Error(`Provider "${dataProviderId}" not registered`);
        }

        const provider = this.providers.get(dataProviderId)!;

        // axis: Get absoluteBoundaries and clamp
        const [min, max] = provider.getAxis(axis).getBoundaries();


        const span = axis == 'x' ? state.spanX : state.spanY;

        // Calculate max allowed cursor (so viewport stays in bounds)
        // Example: maxY=25, span=10 → maxCursorIndex=16 (viewport: [16..25])
        let maxCursorIndex = max - span + 1;

        const currentCursorIndex = axis == 'x' ? state.cursorIndexX : state.cursorIndexY;

        // Apply delta and clamp
        const newCursorIndex = Math.max(min, Math.min(currentCursorIndex + delta, maxCursorIndex));

        Reflect.set(state, `cursorIndex${axis.toUpperCase()}`, newCursorIndex);

        // Increment version to trigger Lit reactive updates
        this._version++;
    }

    /**
     * Updates span (called on resize).
     * Re-clamps cursor if needed to prevent viewport outside absoluteBoundaries.
     *
     * @param key - Provider to update
     * @param spanX - New TXAxisValue span (from outer bounds calculation)
     * @param spanY - New TYAxisValue span (from outer bounds calculation)
     * @throws Error if provider not registered
     */
    updateSpan(key: string, spanX: number, spanY: number): void {
        // TODO: Umstellen auf updateSpan(dataProviderKey: string, axis: 'x'|'y', span: number): void')
        // TODO: x-Achse muss nicht unbedingt Infinity sein -> muss verallgemeinert werden

        const state = this.states.get(key);
        if (!state) {
            throw new Error(`Provider "${key}" not registered`);
        }

        const provider = this.providers.get(key)!;

        // Update spans
        state.spanX = spanX;
        state.spanY = spanY;

        // Get current axis boundaries
        const [minY, maxY] = provider.axisY.getBoundaries();

        // Check if all elements fit in the new viewport size
        // If so, auto-adjust cursor to show all elements from the start
        if (Number.isFinite(minY) && Number.isFinite(maxY)) {
            const totalElementsY = maxY - minY + 1;

            if (totalElementsY <= spanY) {
                // All elements fit - set cursor to minimum to show all
                state.cursorIndexY = minY;
            } else {
                // Not all elements fit - re-clamp cursor to keep viewport in bounds
                const maxCursorY = maxY - spanY + 1;
                state.cursorIndexY = Math.max(minY, Math.min(state.cursorIndexY, maxCursorY));
            }
        }

        // TXAxisValue-axis: Apply same logic if boundaries are finite
        const [minX, maxX] = provider.axisX.getBoundaries();
        if (Number.isFinite(minX) && Number.isFinite(maxX)) {
            const totalElementsX = maxX - minX + 1;

            if (totalElementsX <= spanX) {
                state.cursorIndexX = minX;
            } else {
                const maxCursorX = maxX - spanX + 1;
                state.cursorIndexX = Math.max(minX, Math.min(state.cursorIndexX, maxCursorX));
            }
        }
        // If X-axis is infinite, cursor remains unchanged

        // Increment version to trigger Lit reactive updates
        this._version++;
    }

    /**
     * Notifies the state manager that new data is available for a provider.
     * Increments the version counter to trigger reactive updates in Lit components.
     *
     * @param key - Provider identifier
     * @throws Error if provider not registered
     */
    notifyDataAvailable(key: string): void {
        if (!this.providers.has(key)) {
            throw new Error(`Provider "${key}" not registered`);
        }

        // Increment version to trigger Lit reactive updates
        this._version++;
    }

    /**
     * Gets computed viewport range (for data fetching).
     *
     * @param key - Provider key
     * @returns Viewport as inclusive range { x: {start, end}, y: {start, end} }
     * @throws Error if provider not registered
     */
    getViewport(key: string): MatrixRanges {
        const state = this.states.get(key);
        if (!state) {
            throw new Error(`Provider "${key}" not registered`);
        }

        return {
            x: {start: state.cursorIndexX, end: state.cursorIndexX + state.spanX - 1},
            y: {start: state.cursorIndexY, end: state.cursorIndexY + state.spanY - 1}
        };
    }

    /**
     * Gets raw state for a provider (for debugging/testing).
     *
     * @param key - Provider identifier
     * @returns Provider state or undefined if not found
     */
    getState(key: string): DataProviderState | undefined {
        return this.states.get(key);
    }

    /**
     * Gets a provider instance.
     *
     * @param key - Provider identifier
     * @returns DataProvider instance or undefined if not found
     */
    getProvider(key: string): CrosspointDataProvider<DateTime, unknown, unknown> | undefined {
        return this.providers.get(key);
    }

    /**
     * Gets all registered provider keys.
     *
     * @returns Array of provider identifiers
     */
    getAllProviderKeys(): string[] {
        return Array.from(this.providers.keys());
    }

    /**
     * Clears all registered providers, their states, and the axis-id lookup map.
     */
    clear(): void {
        this.providers.clear();
        this.states.clear();
        this.providerByAxesIdsMap.clear();
    }

    /**
     * Finds and returns a data provider based on the specified TXAxisValue and TYAxisValue axis IDs.
     *
     * @param {string} axisIdX - The identifier for the TXAxisValue-axis.
     * @param {string} axisIdY - The identifier for the TYAxisValue-axis.
     * @return {CrosspointDataProvider<DateTime, unknown, unknown> | null} - The data provider corresponding to the given axis IDs, or null if no matching provider is found.
     */
    findDataProviderByAxesIds(axisIdX: string, axisIdY: string): CrosspointDataProvider<DateTime, unknown, unknown> | null {
        if (!this.providerByAxesIdsMap.has(axisIdX)) {
            return null;
        }

        return this.providers.get(this.providerByAxesIdsMap.get(axisIdX)!.get(axisIdY)!) ?? null;
    }

    /**
     * Finds and returns a list of data providers associated with the specified axis ID.
     *
     * @param {string} axisId - The ID of the axis to search for.
     * @return {CrosspointDataProvider<DateTime, unknown, unknown>[]} An array of data providers that are linked to the specified axis ID.
     */
    findDataProviderByAxisId(axisId: string): CrosspointDataProvider<DateTime, unknown, unknown>[] {
        return [...this.providers.values()].filter(provider => provider.axisX.axisId === axisId || provider.axisY.axisId === axisId);
    }

    whichBoundaryIsCrossedAfterSteps(dataProviderKey: string, axis: 'x' | 'y', steps: number): 'min' | 'max' | 'none' {
        const state = this.states.get(dataProviderKey);
        if (!state) {
            throw new Error(`Provider "${dataProviderKey}" not registered`);
        }

        const dataProvider = this.getProvider(dataProviderKey)!;
        const [min, max] = dataProvider.getAxis(axis).getBoundaries();

        const cursorIndexOfAxis = Reflect.get(state, 'cursorIndex' + axis.toUpperCase()) + steps;
        const spanOfAxis = Reflect.get(state, 'span' + axis.toUpperCase());

        // When all elements fit in viewport, clamp to min boundary
        // to keep first row fully visible and prevent scroll drift
        const totalElements = max - min + 1;
        if (totalElements <= spanOfAxis) {
            return 'min';
        }

        if(cursorIndexOfAxis < min) {
            return 'min';
        }

        if(cursorIndexOfAxis + spanOfAxis - 1 > max) {
            return 'max';
        }

        return 'none';
    }
}
